import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface StructuralRowProps {
  /** Field label shown above the value. */
  label: string;
  /** Plain value for read-only display. Mutually exclusive with ``children``. */
  value?: ReactNode;
  /** Custom rendered content (e.g. ``<code>``, badge, chip list). */
  children?: ReactNode;
  /** Optional hint shown below the value in muted text. */
  hint?: string;
  /** Truncate long values to a single line with ellipsis. */
  truncate?: boolean;
  /** Render value in a monospace block (useful for SQL, column refs). */
  mono?: boolean;
}

/**
 * Read-only key/value row used by the Dashboard tab's inspectors
 * (Iter 21 — replaces the heavy form editors).
 *
 * Layout: small uppercase label on top, value below in a muted card
 * style. Empty values render as ``—``. Use ``children`` for richer
 * content (chips, code blocks, lists); use ``value`` for plain
 * strings or numbers.
 *
 * Example:
 * ```tsx
 * <StructuralRow label="SQL" mono truncate>
 *   <code>{kpi.sql}</code>
 * </StructuralRow>
 * <StructuralRow label="Format" value={kpi.format ?? "default"} />
 * ```
 */
export function StructuralRow({
  label,
  value,
  children,
  hint,
  truncate,
  mono,
}: StructuralRowProps): JSX.Element {
  const body = children ?? value;
  const hasContent =
    body !== null &&
    body !== undefined &&
    (typeof body !== "string" || body.trim().length > 0);

  return (
    <div className="flex flex-col gap-0.5 py-1.5">
      <span className="text-xs font-semibold text-neutral-500">
        {label}
      </span>
      <div
        className={cn(
          "text-xs text-neutral-800",
          truncate && "truncate",
          mono && "font-mono",
        )}
        title={truncate && typeof body === "string" ? body : undefined}
      >
        {hasContent ? body : <em className="text-neutral-500">—</em>}
      </div>
      {hint && <p className="text-xs text-neutral-500">{hint}</p>}
    </div>
  );
}
